import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ComparePage from '../area/ComparePage.js';
import { comparePath } from './areaShare.js';
import { reorderComparedAreas } from './compareAreas.js';
import './comparePreview.css';

// The comparison card's "Jämför i storvy" (CompareCard.js): the compared areas against each other in a window over
// the view — the same page a shared comparison opens (ComparePage.js, /jamfor/<codes>), so what you see is what
// you'd send. Like a listing's popup: a click outside it, its ✕ or Escape closes it, and Back does too (opening
// pushes a history entry carrying the comparison's own link, closing another way takes it off). Over kartvy its map
// buttons pick the areas on that map (onShowOnMap); over listvy "Visa alla bostäder" makes them the search there
// (onShowHomes). A column dragged to another place there reorders the comparison itself (the card and the map's
// numbers follow) and the link in the address bar.

const CLOSE_ICON = (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" />
    </svg>
);

export default function ComparePreview({ codes: openedWith, languageState, onClose, onShowOnMap = null, onShowHomes = null }) {
    const swedish = Boolean(languageState?.swedish);
    const [codes, setCodes] = useState(openedWith);
    const reorder = useCallback((order) => {
        setCodes(order);
        reorderComparedAreas(order);
        if (window.history.state?.comparePreview) window.history.replaceState(window.history.state, '', comparePath(order));
    }, []);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const shellRef = useRef(null);

    // Back closes it; any other close takes its history entry off again.
    useEffect(() => {
        if (typeof window === 'undefined' || !window.history?.pushState) return undefined;
        let entryOnTop = true;
        window.history.pushState({ ...(window.history.state || {}), comparePreview: true }, '', comparePath(codes));
        const onPopState = () => {
            entryOnTop = false;
            onCloseRef.current?.();
        };
        window.addEventListener('popstate', onPopState);
        return () => {
            window.removeEventListener('popstate', onPopState);
            if (entryOnTop && window.history.state?.comparePreview) window.history.back();
        };
        // One entry per opening.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onKey = (event) => { if (event.key === 'Escape') onCloseRef.current?.(); };
        document.addEventListener('keydown', onKey);
        shellRef.current?.focus({ preventScroll: true });
        // The page under it doesn't scroll along.
        const { overflow } = document.body.style;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = overflow;
        };
    }, []);

    return createPortal(
        <div className="compare-preview-overlay" onClick={() => onCloseRef.current?.()} role="presentation">
            <section
                ref={shellRef}
                tabIndex={-1}
                className="compare-preview-shell"
                role="dialog"
                aria-modal="true"
                aria-label={swedish ? 'Jämförelsen' : 'The comparison'}
                onClick={(event) => event.stopPropagation()}
            >
                <button type="button" className="compare-preview-close" onClick={() => onCloseRef.current?.()} aria-label={swedish ? 'Stäng' : 'Close'}>
                    {CLOSE_ICON}
                </button>
                <ComparePage languageState={languageState} codes={codes} preview onReorder={reorder} onShowOnMap={onShowOnMap} onShowHomes={onShowHomes} onClosePreview={() => onCloseRef.current?.()} />
            </section>
        </div>,
        document.body,
    );
}
